// src/pages/Upgrade.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Smartphone, Star, BadgeCheck } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

export default function Upgrade() {
  // Using SubscriptionContext
  const { isPro, isTrialActive, isDev, loading } = useSubscription();

  return (
    <div className="min-h-screen bg-[#f4f7f9] dark:bg-black text-gray-900 dark:text-gray-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-3xl shadow-xl p-8 md:p-10 space-y-8 border border-gray-100 dark:border-zinc-800">
        
        {/* Back */}
        <div className="flex justify-between items-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to dashboard
          </Link>

          {/* 🌟 DEV MODE BADGE */}
          {isDev && (
            <span className="px-3 py-1 rounded-full bg-purple-600/20 border border-purple-400 text-purple-300 text-xs font-semibold backdrop-blur-md shadow">
              DEV MODE
            </span>
          )}
        </div>

        {/* Title */}
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Unlock LumoChart&nbsp;
            <span className="bg-gradient-to-r from-blue-500 to-blue-300 bg-clip-text text-transparent">
              Pro
            </span>
          </h1>

          <p className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed">
            Full note generation, PDF export, inpatient/ED/consult tools, and advanced
            disposition calculators are available to{' '}
            <span className="font-semibold">LumoChart Pro</span> subscribers.
          </p>

          {/* Subscription Status */}
          {!loading && (
            <>
              {/* 🌟 DEV MODE — full access */}
              {isDev && (
                <p className="text-purple-400 font-semibold text-sm md:text-base flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5" />
                  Developer mode is active — you have full unrestricted access.
                </p>
              )}

              {/* Normal trial/pro messaging */}
              {!isDev && isTrialActive && (
                <p className="text-green-600 dark:text-green-400 font-semibold text-sm md:text-base">
                  🎉 Your free trial is active — enjoy full access.
                </p>
              )}

              {!isDev && isPro && (
                <p className="text-blue-600 dark:text-blue-400 font-semibold text-sm md:text-base">
                  You already have LumoChart Pro on iOS.
                </p>
              )}
            </>
          )}
        </div>

        {/* DEV USERS DO NOT SEE PLANS OR FEATURES */}
        {!isDev && (
          <>
            {/* Feature Grid */}
            <div className="grid md:grid-cols-3 gap-4">
              <UpgradeCard
                icon={Lock}
                title="Full note editors"
                text="Generate, edit, and export admission, progress, ED, consult, and discharge notes."
              />
              <UpgradeCard
                icon={Star}
                title="Advanced tools"
                text="Access insulin, steroid, opioid, and disposition tools directly from the chart."
              />
              <UpgradeCard
                icon={Smartphone}
                title="Managed in iOS"
                text="Subscriptions are securely managed through the LumoChart iOS app."
              />
            </div>

            {/* Steps */}
            <div className="space-y-3">
              <h2 className="text-xl font-semibold">How to upgrade</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 text-sm md:text-base">
                <li>
                  Open the <span className="font-semibold">LumoChart</span> app on your iPhone or iPad.
                </li>
                <li>
                  Go to <span className="font-semibold">Settings → Subscription</span>.
                </li>
                <li>
                  Select a <span className="font-semibold">LumoChart Pro</span> plan and complete the purchase.
                </li>
                <li>Sign in here on the web using the same account.</li>
              </ol>
            </div>
          </>
        )}

        {/* DEV FOOTNOTE */}
        {isDev && (
          <p className="text-xs text-purple-300 text-center mt-6">
            Developer override is enabled. Users will not see Dev Mode.
          </p>
        )}

        {!isDev && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            All billing is handled by Apple via the App Store. The web app never stores payment details.
          </p>
        )}
      </div>
    </div>
  );
}

function UpgradeCard({ icon: Icon, title, text }) {
  return (
    <div className="rounded-2xl border border-gray-100 dark:border-zinc-700 bg-gray-50/70 dark:bg-zinc-900/60 p-4 flex flex-col gap-2">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 mb-1">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-semibold text-sm md:text-base">{title}</h3>
      <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{text}</p>
    </div>
  );
}
